import { createSelector } from "@reduxjs/toolkit";
import { markupsSelector } from "@store/markups/markups-selector";
import { companyTeamsAsMemberSelector } from "@store/members/members-selector";
import { selectedProjectSnapshotsSelector } from "@store/projects/projects-selector";
import { TableAdapter } from "@store/table/table-slice";
/** Returns the bulk action table type */
export const tableTypeSelector = createSelector(state => state.table, table => {
  return table.tableType;
});

/** Returns the bulk action name */
export const bulkActionNameSelector = createSelector(state => state.table, table => {
  return table.bulkActionName;
});

/** Returns the bulk action selected row Ids */
export const selectedRowIdsSelector = createSelector(state => state.table, table => {
  return TableAdapter.getSelectors().selectIds(table);
});

/** Returns the bulk action results */
export const bulkActionResultsSelector = createSelector(state => state.table, table => {
  return table.bulkActionResults;
});

/** Returns all updating items */
export const updatingItemsSelector = createSelector(state => state.table, table => {
  return TableAdapter.getSelectors().selectAll(table);
});

/** Returns the total number of selected items */
export const numberOfUpdatingItemsSelector = createSelector(state => state.table, table => {
  return TableAdapter.getSelectors().selectTotal(table);
});

// All the possible types to use in the selectedEntitiesSelector

/** Receive entity type and return all the selected items in table  */
export function selectedEntitiesSelector(bulkActionEntityType) {
  switch (bulkActionEntityType) {
    case "projects":
      return selectedProjectEntitiesSelector;
    case "members":
      return selectedMemberEntitiesSelector;
    case "groups":
      return selectedGroupEntitiesSelector;
    case "teams":
      return selectedTeamEntitiesSelector;
    case "markups":
      return selectedMarkupEntitiesSelector;
    case "mapProjects":
      return selectedMapProjectEntitiesSelector;
    case "snapshots":
      return selectedSnapshotEntitiesSelector;
    default:
      throw new Error(`Unknown context type: ${bulkActionEntityType}`);
  }
}

/**
 * Return all the member entities based on the updating items ID.
 *
 * The table identifies a row by the identity of the member, while the store keys a member by their
 * ID. Both are the same for a member that joined the workspace, but a pending member that already
 * has an ID is identified by their email, so they are looked up by their identity as well.
 */
const selectedMemberEntitiesSelector = createSelector(selectedRowIdsSelector, state => state.members.entities, (selectedRowIds, memberEntities) => {
  const membersByIdentity = new Map(Object.values(memberEntities).map(member => [member.identity, member]));
  return selectedRowIds.reduce((selectedEntities, selectedRow) => {
    const companyMember = memberEntities[selectedRow] ?? membersByIdentity.get(selectedRow);
    if (companyMember) {
      selectedEntities.push(companyMember);
    }
    return selectedEntities;
  }, []);
});

/**
 * Return all the project entities based on the updating items ID.
 *
 * The filtered projects list shows projects from the projects stream, so a project that was not
 * fetched page by page is looked up in the projects map as well.
 */
const selectedProjectEntitiesSelector = createSelector(selectedRowIdsSelector, state => state.projects.entities, state => state.projectsMap.entities, (selectedRowIds, projectEntities, mapProjectEntities) => {
  return selectedRowIds.map(selectedRowId => projectEntities[selectedRowId] ?? mapProjectEntities[selectedRowId]).filter(project => !!project);
});

/** Return all the group entities based on the updating items ID */
const selectedGroupEntitiesSelector = createSelector(selectedRowIdsSelector, state => state.groups.entities, (selectedRowIds, groupEntities) => {
  return selectedRowIds.reduce((selectedEntities, selectedRow) => {
    const group = groupEntities[selectedRow];
    if (group) {
      selectedEntities.push(group);
    }
    return selectedEntities;
  }, []);
});

/** Return all the team entities based on the updating items ID */
const selectedTeamEntitiesSelector = createSelector(selectedRowIdsSelector, state => state.teams.entities, companyTeamsAsMemberSelector, (selectedRowIds, teamEntities, companyTeamsAsMember) => {
  return selectedRowIds.reduce((selectedEntities, selectedRow) => {
    const team = teamEntities[selectedRow];
    if (team) {
      const teamAsMember = companyTeamsAsMember.find(teamAsMember => teamAsMember.identity === team.id);
      selectedEntities.push({
        ...team,
        role: teamAsMember?.role ?? "MEMBER"
      });
    }
    return selectedEntities;
  }, []);
});

/** Return all the markup entities based on the updating items ID */
const selectedMarkupEntitiesSelector = createSelector(selectedRowIdsSelector, markupsSelector, (selectedRowIds, markups) => {
  return selectedRowIds.reduce((selectedEntities, selectedRow) => {
    const markup = markups.find(m => m.id === selectedRow);
    if (markup) {
      selectedEntities.push(markup);
    }
    return selectedEntities;
  }, []);
});

/**
 * @returns all the map project entities based on the updating items ID
 */
const selectedMapProjectEntitiesSelector = createSelector(selectedRowIdsSelector, state => state.projectsMap.entities, (selectedRowIds, mapProjectEntities) => {
  return selectedRowIds.map(selectedRowId => mapProjectEntities[selectedRowId]).filter(project => !!project);
});

/**
 * @returns all the snapshot entities based on the updating items ID
 */
const selectedSnapshotEntitiesSelector = createSelector(selectedRowIdsSelector, selectedProjectSnapshotsSelector, (selectedRowIds, snapshots) => {
  return selectedRowIds.map(selectedRowId => snapshots.find(s => s.id === selectedRowId)).filter(snapshot => !!snapshot);
});

/** Returns whether any bulk action is in the provided status or not */
export function isUpdatingItemsInStatusStageSelector(status) {
  return createSelector(updatingItemsSelector, updatingItems => {
    return updatingItems.some(item => item.status === status);
  });
}

/** Returns whether any bulk action is still in progress or not */
export const isBulkActionProcessingSelector = createSelector(isUpdatingItemsInStatusStageSelector("updating"), isUpdatingItemsInStatusStageSelector("in-queue"), (isUpdating, isInQueue) => {
  return isUpdating || isInQueue;
});

/** Whether to show the success dialog or not */
export const shouldShowSuccessDialogSelector = createSelector(state => state.table.bulkActionResults, bulkActionResults => {
  return bulkActionResults.numberOfErrors === 0 && bulkActionResults.numberOfSuccess > 0;
});

/** Whether to show the failed dialog or not */
export const shouldShowFailedDialogSelector = createSelector(state => state.table.bulkActionResults, bulkActionResults => {
  return bulkActionResults.numberOfErrors > 0;
});

/** Allowed bulk actions in a session */
export const allowedBulkButtonsInSessionSelector = createSelector(state => state.table, table => {
  return table.allowedBulkButtonsInSession;
});